import { SignInPage } from '@backstage/core-components';
import { OAuth2 } from '@backstage/core-app-api';
import {
  BackstageIdentityApi,
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  configApiRef,
  createApiFactory,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
} from '@backstage/plugin-app-react';

/** Sign-in with the Keycloak realm `platform` (provider `oidc` of the auth backend, see app-config.production.yaml). */
export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({ id: 'internal.auth.oidc' });

const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: define =>
    define(
      createApiFactory({
        api: oidcAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            provider: { id: 'oidc', title: 'Keycloak', icon: () => null },
            environment: configApi.getOptionalString('auth.environment'),
            defaultScopes: ['openid', 'profile', 'email'],
          }),
      }),
    ),
});

const OidcSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  // Local development has no Keycloak: guest provider only there.
  if (configApi.getOptionalString('auth.environment') === 'development') {
    return <SignInPage {...props} providers={['guest']} />;
  }
  return (
    <SignInPage
      {...props}
      provider={{
        id: 'oidc',
        title: 'Keycloak',
        message: 'Sign in with your Keycloak account',
        apiRef: oidcAuthApiRef,
      }}
    />
  );
};

const oidcSignInPage = SignInPageBlueprint.make({
  params: { loader: async () => OidcSignInPage },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcAuthApi, oidcSignInPage],
});
